import type { StringGUID } from "@pdfking/tfunc"
import { AtomHash, FullMethodDispatcher, GuidToString, Items } from "@pdfking/tfunc"

import type { VirtualFile } from "#public"

import { VirtualFileType } from "../types/files/VirtualFileType"

const get_content_guid = (file: { content: { guid: StringGUID } }) => file.content.guid

/** Which bytes a file is, from what it is made of — never from a plan.
 *
 * A file with bytes of its own is named by them; a lazy one by its parts:
 * a merge by its pages in order, a zip by its members' names and identities,
 * a plot by the guid whoever made its shapes gave it. The task that produces
 * a lazy file's bytes writes them under this name (see FilePlan), so a
 * re-run lands where the first run did. The enum leads every hash so that
 * different files over the same parts are different names. */
export const FileContentGUID: (file: VirtualFile) => StringGUID = FullMethodDispatcher<
  [VirtualFile],
  StringGUID
>({
  [VirtualFileType.BINARY_PAGE_PDF]: get_content_guid,
  [VirtualFileType.MULTI_PAGE_PDF]: file =>
    GuidToString(AtomHash([file.enum, ...file.pages.map(FileContentGUID)])),
  [VirtualFileType.PLOTTED_PAGE_PDF]: file => file.guid,
  [VirtualFileType.REMOTE_FILE]: file => file.guid,
  [VirtualFileType.SINGLE_PAGE_IMAGE]: get_content_guid,
  [VirtualFileType.ZIP_FILE]: file =>
    GuidToString(
      AtomHash([
        file.enum,
        ...Items(file.files).flatMap(([name, member]) => [name, FileContentGUID(member)]),
      ])
    ),
})
